/**
 * Map Library Plugin
 *
 * Plugin-host–compatible plugin that loads the map provider SDK,
 * creates the map adapter, and exposes the facade on context.services.
 *
 * @module libraries/mapLibraryPlugin
 */

import createGoogleMapsAdapter from './googleMapsAdapter';
import { GRID_SIZE } from './mapLibrary/utils';

function createMapAdapter({ provider }) {
  if (provider === 'google') {
    return createGoogleMapsAdapter();
  }

  throw new Error('Unsupported map provider: ' + provider);
}

/**
 * Cell size (in decimal degrees) used to bucket map positions into a spatial grid.
 * A smaller value produces finer-grained cells; a larger value produces coarser cells.
 * Used by {@link getSpatialKey} to generate consistent grid keys for nearby coordinates.
 */
export const MAP_GRID_SIZE = GRID_SIZE; // Re-export grid size constant for plugin use

export default function createMapLibraryPlugin({ provider = 'google', config = {} } = {}) {
  let adapter = null;

  return {
    name: 'map-library',

    async init(context) {
      context.state.map = {
        provider,
        ready: false,
      };
    },

    async start(context) {
      if (context.services.mapFacade) {
        return;
      }

      adapter = createMapAdapter({ provider });
      await adapter.load({ config });

      context.services.mapFacade = adapter.createFacade();
      context.state.map = {
        provider,
        ready: true,
      };
    },

    async stop(context) {
      await adapter?.destroy?.();
      adapter = null;
      delete context.services.mapFacade;
      delete context.state.map;
    },
  };
}